import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, Unlock } from 'lucide-react';
import * as facturacion from '@/api/facturacion.api';
import type { ConfigDeFacturacion, Secuencia, TipoDeNcf } from '@/api/facturacion.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Toggle } from '@/components/ui/Toggle';
import { useToast } from '@/hooks/useToast';

/**
 * AJUSTES DE FACTURACIÓN — solo de quien administra el sistema.
 *
 * Los datos del emisor, el ITBIS y los comprobantes fiscales. El servidor se
 * lo niega (403) a la cuenta de la clienta; el botón que abre esto tampoco le
 * sale.
 *
 * Los comprobantes fiscales vienen APAGADOS y sin secuencias: el RNC y los
 * rangos los da la DGII al negocio, y aquí se copian tal cual. No se inventan.
 *
 * Con los comprobantes ya encendidos, el RNC y las secuencias abren con
 * candado (como en FacturApp): mover un contador por descuido es emitir un
 * comprobante repetido o saltarse uno.
 */

const TIPOS: Array<{ tipo: TipoDeNcf; nombre: string; para: string }> = [
  { tipo: 'B02', nombre: 'B02 · Consumo', para: 'Ventas a consumidores finales.' },
  { tipo: 'B01', nombre: 'B01 · Crédito fiscal', para: 'Ventas a empresas con RNC, que usan el ITBIS como crédito.' },
  { tipo: 'B14', nombre: 'B14 · Régimen especial', para: 'Zonas francas y regímenes especiales.' },
  { tipo: 'B15', nombre: 'B15 · Gubernamental', para: 'Ventas al Estado.' },
];

export function AjustesDeFacturacion({ onCerrar }: { onCerrar: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['facturacion', 'config'], queryFn: facturacion.leerConfig });
  const [c, setC] = useState<ConfigDeFacturacion | null>(null);
  const [candado, setCandado] = useState(false);
  const [abriendo, setAbriendo] = useState(false);
  useEffect(() => {
    if (!data) return;
    setC(data);
    setCandado(data.ncf.activo);
  }, [data]);

  const guardar = useMutation({
    mutationFn: () => facturacion.guardarConfig(c!),
    onSuccess: () => {
      toast.success('Ajustes de facturación guardados.');
      void qc.invalidateQueries({ queryKey: ['facturacion'] });
      onCerrar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo guardar'),
  });

  if (!c) {
    return (
      <Modal open onClose={onCerrar} title="Ajustes de facturación">
        <p className="py-6 text-center text-sm text-slate-500">Cargando…</p>
      </Modal>
    );
  }

  const emisor = (campo: keyof ConfigDeFacturacion['emisor'], valor: string) =>
    setC({ ...c, emisor: { ...c.emisor, [campo]: valor } });

  const secuenciaDe = (tipo: TipoDeNcf) => c.ncf.secuencias.find((s) => s.tipo === tipo);
  const ponerSecuencia = (tipo: TipoDeNcf, cambio: Partial<Secuencia> | null) => {
    const resto = c.ncf.secuencias.filter((s) => s.tipo !== tipo);
    if (!cambio) return setC({ ...c, ncf: { ...c.ncf, secuencias: resto } });
    const base = secuenciaDe(tipo) ?? { tipo, desde: 1, hasta: 1, siguiente: 1, vence: null };
    setC({ ...c, ncf: { ...c.ncf, secuencias: [...resto, { ...base, ...cambio }] } });
  };
  const entero = (v: string) => Math.max(1, Math.floor(Number(v) || 1));

  return (
    <Modal open onClose={onCerrar} title="Ajustes de facturación" size="lg">
      <div className="space-y-6">
        <section className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Quién emite
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Nombre o razón social"
              value={c.emisor.nombre}
              onChange={(e) => emisor('nombre', e.target.value)}
              hint="Vacío: sale el nombre del negocio."
            />
            <Input
              label="RNC"
              value={c.emisor.rnc}
              onChange={(e) => emisor('rnc', e.target.value)}
              inputMode="numeric"
              placeholder="Sin RNC todavía"
              disabled={candado}
            />
            <Input
              label="Dirección"
              value={c.emisor.direccion}
              onChange={(e) => emisor('direccion', e.target.value)}
            />
            <Input
              label="Teléfono"
              value={c.emisor.telefono}
              onChange={(e) => emisor('telefono', e.target.value)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
            <Input
              label="Pie de la factura"
              value={c.pie}
              onChange={(e) => setC({ ...c, pie: e.target.value })}
              placeholder="Política de cambios, cuenta para transferir…"
            />
            <Input
              label="Días para pagar"
              type="number"
              value={String(c.diasDeVencimiento)}
              onChange={(e) =>
                setC({
                  ...c,
                  diasDeVencimiento: Math.min(365, Math.max(0, Math.floor(Number(e.target.value) || 0))),
                })
              }
              hint="0: se paga el mismo día."
            />
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">ITBIS</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Los precios ya lo incluyen; la factura lo desglosa al centavo. El total no cambia.
              </p>
            </div>
            <Toggle
              label="Desglosar ITBIS"
              checked={c.itbis.activo}
              onChange={(activo) => setC({ ...c, itbis: { ...c.itbis, activo } })}
            />
          </div>
          {c.itbis.activo && (
            <Input
              label="Tasa (%)"
              type="number"
              className="max-w-[8rem]"
              value={String(c.itbis.tasa)}
              onChange={(e) =>
                setC({
                  ...c,
                  itbis: { ...c.itbis, tasa: Math.min(30, Math.max(0, Math.floor(Number(e.target.value) || 0))) },
                })
              }
            />
          )}
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Comprobantes fiscales (NCF)
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Apagados hasta tener el RNC y las secuencias que autorizó la DGII. Se copian tal
                cual vienen en la autorización.
              </p>
            </div>
            <Toggle
              label="Emitir con NCF"
              checked={c.ncf.activo}
              disabled={candado}
              onChange={(activo) => setC({ ...c, ncf: { ...c.ncf, activo } })}
            />
          </div>

          {candado && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-700 dark:bg-amber-900/30">
              <p className="flex min-w-0 flex-1 items-start gap-2 text-sm text-amber-900 dark:text-amber-100">
                <Lock className="mt-0.5 h-4 w-4 shrink-0" />
                {abriendo
                  ? 'Un número mal puesto emite un comprobante repetido o salta uno, y eso se reporta a la DGII. ¿Abrir?'
                  : 'El RNC y las secuencias están con candado: ya se emiten comprobantes con ellos.'}
              </p>
              {abriendo ? (
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setAbriendo(false)}>
                    No
                  </Button>
                  <Button size="sm" onClick={() => setCandado(false)}>
                    Sí, abrir
                  </Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setAbriendo(true)}>
                  <Unlock className="h-3.5 w-3.5" />
                  Quitar el candado
                </Button>
              )}
            </div>
          )}

          {TIPOS.map(({ tipo, nombre, para }) => {
            const s = secuenciaDe(tipo);
            return (
              <div key={tipo} className="rounded-lg border border-surface-border p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{nombre}</p>
                    <p className="text-xs text-slate-500">{para}</p>
                  </div>
                  <Toggle
                    label={`Secuencia ${tipo}`}
                    checked={Boolean(s)}
                    disabled={candado}
                    onChange={(on) => ponerSecuencia(tipo, on ? {} : null)}
                  />
                </div>
                {s && (
                  <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Input
                      label="Desde"
                      type="number"
                      disabled={candado}
                      value={String(s.desde)}
                      onChange={(e) => ponerSecuencia(tipo, { desde: entero(e.target.value) })}
                    />
                    <Input
                      label="Hasta"
                      type="number"
                      disabled={candado}
                      value={String(s.hasta)}
                      onChange={(e) => ponerSecuencia(tipo, { hasta: entero(e.target.value) })}
                    />
                    <Input
                      label="Próximo"
                      type="number"
                      disabled={candado}
                      value={String(s.siguiente)}
                      onChange={(e) => ponerSecuencia(tipo, { siguiente: entero(e.target.value) })}
                    />
                    <Input
                      label="Vence"
                      type="date"
                      disabled={candado}
                      value={s.vence ?? ''}
                      onChange={(e) => ponerSecuencia(tipo, { vence: e.target.value || null })}
                    />
                  </div>
                )}
              </div>
            );
          })}
          <p className="text-xs text-slate-500">
            El número de cada comprobante se toma al guardar la factura, uno a la vez: «Próximo» es
            solo el punto de partida. La factura electrónica (e-CF: E31, E32, E33) no está en este
            sistema: pide certificación y firma digital ante la DGII.
          </p>
        </section>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => guardar.mutate()} disabled={guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
